import * as rxjs from 'rxjs';
import * as Mutations from './Mutations';
import * as Elevated from '@pitaman71/omniglot-introspect';

import * as Streams from './Streams';
import * as Objects from './Objects';
import * as Operations from './Operations';
import * as Properties from './Properties';
import * as Relations from './Relations';

const __moduleName__ = `omniglot-live-data.Stores`;

export class Key<PropertyTypeParams extends Properties.TypeParams> extends Elevated.Serializable {
    property?: Properties.Descriptor<PropertyTypeParams>;
    relation?: Relations.Descriptor<PropertyTypeParams["Binding"]>;
    binding: Partial<PropertyTypeParams["Binding"]>;

    constructor() {
        super();
        this.binding = {};
    }

    static ClassSpec = `${__moduleName__}.Key`;
    static Factory = Elevated.factories.concrete<Key<any>>(Key.ClassSpec, () => new Key());
    getFactory = () => Key.Factory;
    getGlobalId(): number|string|null { return null; }

    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.verbatim(this,(key: any) => ({
            '__class__': Key.ClassSpec,
            property: key.property?.canonicalName,
            relation: key.relation?.canonicalName,
            binding: key.binding
        }), (target, json: any) => {
            this.property = !json.property ? undefined : Properties.Descriptor.Directory.find(json.property);
            this.relation = !json.relation ? undefined : Relations.Descriptor.Directory.find(json.relation);
            this.binding = json.binding;
        })
        visitor.end(this);
    }

    static fromEmpty(
    ) : Key<any> {
        return new Key();
    }

    static fromProperty<PropertyTypeParams extends Properties.TypeParams>(
        property: Properties.Descriptor<PropertyTypeParams>,
        binding: Partial<PropertyTypeParams["Binding"]>
    ) : Key<PropertyTypeParams> {
        const result = new Key<PropertyTypeParams>();
        result.property = property;
        result.binding = Object.assign({}, binding);
        return result;
    }

    static fromRelation<BindingType extends Objects.BindingType>(
        relation: Relations.Descriptor<BindingType>,
        binding: Partial<BindingType>
    ) : Key<Properties.TypeParams<BindingType, any, any>> {
        const result = new Key<Properties.TypeParams<BindingType, any, any>>();
        result.relation = relation;
        result.binding = Object.assign({}, binding);
        return result;
    }

    isConsistent(other: Partial<PropertyTypeParams["Binding"]>) {
        for(let key of Object.getOwnPropertyNames(this.binding)) {
            if(other[key] == undefined && this.binding[key] !== undefined && other[key]?.objectId !== this.binding[key]?.objectId) return false;
        }
        return true;
    }

    merge(other: Partial<PropertyTypeParams["Binding"]>) {
        return {
            ... this.binding,
            ... other.binding
        };
    }

    getCanonicalName(): string {
        return (this.property?.canonicalName || this.relation?.canonicalName || '');
    }

    toString(): string {
        if(this.property === undefined && this.relation === undefined) {
            return '';
        }
        try {
            const asString = JSON.stringify(this.binding);
            return (this.property?.canonicalName || this.relation?.canonicalName)+asString;
        } catch(e: any) {
            throw new Error(e.toString());
        }
    }

    asObject(options: { from: boolean, to: boolean } = { from: true, to: true }) {
        const target = this;
        return new class {
            to(): any {
                let result:any = { 
                    __property__: target.property?.canonicalName,
                    __relation__: target.relation?.canonicalName
                }
                if(target.property !== undefined) {
                    target.property.getSymbols().forEach((symbol) => {
                        result[symbol.toString()] = target.binding[symbol] ? target.binding[symbol].objectId : undefined;
                    });
                }
                if(target.relation !== undefined) {
                    target.relation.getSymbols(options).forEach((symbol) => {
                        result[symbol.toString()] = target.binding[symbol] ? target.binding[symbol].objectId : undefined;
                    });
                }
                return result;        
            }
            from(value: any) {
                if(target.property !== undefined) {
                    target.property.getSymbols().forEach((symbol) => {
                        target.binding[symbol] = Objects.Binding.from_bound(value[symbol.toString()]);
                    });
                }
                if(target.relation !== undefined) {
                    target.relation.getSymbols(options).forEach((symbol) => {
                        target.binding[symbol] = Objects.Binding.from_bound(value[symbol.toString()]);
                    });
                }
            }
        }
    }
}

export interface Persist {
    onPullRequest: rxjs.Subject<any>;
    clear() : Promise<void>;
    replay<MutationType extends Mutations.BaseMutation>(
        key: Key<any>,
        receive: rxjs.Observer<MutationType>
    ): Promise<Elevated.Reference<MutationType>>;
    pull<MutationType extends Mutations.BaseMutation> (
        key: Key<any>,
        since: Elevated.Reference<MutationType>,
        receive: rxjs.Observer<MutationType>,
    ): Promise<Elevated.Reference<MutationType>>;
    push<MutationType extends Mutations.BaseMutation> (
        key: Key<any>,
        ...suffix: MutationType[]
    ): Promise<MutationType>;
    normalize(key: Key<any>) : Promise<any>;
}

export abstract class Zone {
    _properties: Map<string, Streams.PropertyStream<any>> = new Map();
    _relations: Map<string, Streams.RelationStream<any>> = new Map();
    _peerId: string = Elevated.factories.makeObjectId();

    abstract streams(): Streams.Factory;

    _statefuls(): Streams.Stateful<Mutations.BaseMutation>[] {
        const result: Streams.Stateful<Mutations.BaseMutation>[] = [];
        for(let [ persistKey, mutator ] of this._properties) {
            const stateful = 
                mutator.scalar?.stateful()
                || mutator.set?.stateful()
                || mutator.sequence?.stateful()
                || mutator.map?.stateful();
            if(stateful !== undefined) {
                result.push(stateful);
            }
        }
        for(let [ persistKey, mutator ] of this._relations) {
            const stateful = mutator.stateful();
            if(stateful)
                result.push(stateful);
        }
        return result;
    }
    
    getAllMutations(): Mutations.BaseMutation[] {
        const statefuls = this._statefuls();
        const unique = new Set<Streams.Stateful<Mutations.BaseMutation>>(statefuls);
        let mutations: Mutations.BaseMutation[] = [];
        for(let stateful of unique) {
            mutations = [ ... mutations, ... stateful.getChanges() ];
        }
        return mutations;
    }

    commitAll() : Promise<any> {
        const statefuls = this._statefuls();
        const unique = new Set<Streams.Stateful<Mutations.BaseMutation>>(statefuls);
        return Promise.all(Array.from(unique).map(stateful => stateful.commit()));
    }
    
    revertAll() : Promise<any> {
        const statefuls = this._statefuls();
        const unique = new Set<Streams.Stateful<Mutations.BaseMutation>>(statefuls);
        return Promise.all(Array.from(unique).map(stateful => stateful.revert()));
    }
}
